// src/components/modals/CloseShiftModal.jsx
// Modal for closing shift with cash balance

import { useState } from 'react'
import { X } from 'lucide-react'
import PropTypes from 'prop-types'

const CloseShiftModal = ({ isOpen, onClose, onConfirm, openBalance, loading = false }) => {
  const [closeBalance, setCloseBalance] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!closeBalance || parseFloat(closeBalance) < 0) {
      return
    }

    onConfirm(closeBalance)
  }

  const handleClose = () => {
    setCloseBalance('')
    onClose()
  }

  if (!isOpen) return null

  const difference = closeBalance ? parseFloat(closeBalance) - parseFloat(openBalance || 0) : 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Close Shift</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Opening Balance Display */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Opening Balance</div>
            <div className="text-lg font-semibold text-gray-900">Rs. {parseFloat(openBalance || 0).toFixed(2)}</div>
          </div>

          {/* Closing Balance Input */}
          <div className="mb-4">
            <label htmlFor="closeBalance" className="block text-sm font-medium text-gray-700 mb-2">
              Closing Cash Balance
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                Rs.
              </span>
              <input
                type="number"
                id="closeBalance"
                value={closeBalance}
                onChange={(e) => setCloseBalance(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                placeholder="0.00"
                min="0"
                step="0.01"
                required
                disabled={loading}
                autoFocus
              />
            </div>
          </div>

          {/* Difference Display */}
          {closeBalance && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Difference</div>
              <div className={`text-lg font-semibold ${difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {difference >= 0 ? '+' : ''}Rs. {difference.toFixed(2)}
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !closeBalance || parseFloat(closeBalance) < 0}
            >
              {loading ? 'Closing...' : 'Close Shift'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

CloseShiftModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  openBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool
}

export default CloseShiftModal
